import { useMemo, type ReactNode } from "react";
import Title from "@/components/Title";
import {
  QueryTable,
  type ListProps,
} from "@/components/query-table/QueryTable";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import type { DataTableQuery } from "@/types/dataTableQuery";
import type { UseDataTableOptions } from "@/types/dataTableSettings";
import type { SuccessFetchListResponse } from "@/services/api";
import {
  commentReadSchemaReadonly,
  type CommentRead,
} from "@/schemas/comments";
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { getRowPropsWithDraftHighlight } from "../utils/getRowPropsWithDraftHighlight";
import { useCommentsTableColumnOverrides } from "../hooks/useCommentsTableColumnOverrides";
import { staticCommentsToQuery } from "../utils/staticCommentsToQuery";

export type CommentsListQuery<TRow = CommentRead> =
  | UseInfiniteQueryResult<
      InfiniteData<SuccessFetchListResponse<TRow>>,
      unknown
    >
  | UseQueryResult<SuccessFetchListResponse<TRow>>;

export interface CommentsListProps {
  query?: CommentsListQuery<unknown>;
  staticComments?: CommentRead[];
  staticCommentsLoading?: boolean;
  options: UseDataTableOptions<CommentRead>;
  title?: ReactNode;
  name?: string;
  initialColumnVisibility?: Record<string, boolean>;
  dataMapper?: (data: unknown[]) => CommentRead[];
  footer?: ReactNode;
  draftCommentIds?: Set<string>;
  onRemoveDraft?: (id: string) => void;
  onDraftCommentChange?: (id: string, value: string) => void;
}

export function CommentsList({
  query: queryProp,
  staticComments,
  staticCommentsLoading = false,
  options,
  title,
  name = "comments",
  initialColumnVisibility,
  dataMapper,
  footer,
  draftCommentIds,
  onRemoveDraft,
  onDraftCommentChange,
}: CommentsListProps) {
  const query = useMemo(() => {
    if (staticComments !== undefined) {
      return staticCommentsToQuery(staticComments, staticCommentsLoading);
    }
    return queryProp;
  }, [queryProp, staticComments, staticCommentsLoading]);

  const columnOverrides = useCommentsTableColumnOverrides({
    draftCommentIds,
    onRemoveDraft,
    onDraftCommentChange,
  });

  const queryValue = useMemo(
    () => ({ query }) as DataTableQuery<unknown>,
    [query],
  );

  const tableFeatures = useMemo(
    () => ({
      enableSorting: false,
      enableFilters: false,
      enableColumnPinning: false,
      enableColumnOrder: false,
      enableColumnVisibility: false,
      initialColumnVisibility,
      getRowProps: getRowPropsWithDraftHighlight(draftCommentIds),
    }),
    [draftCommentIds, initialColumnVisibility],
  );

  if (query == null) {
    return null;
  }

  return (
    // grid makes the single child stretch so height propagates to DataTable's slate-200 wrapper.
    <div className="grid">
      <QueryTable<CommentRead>
        schema={commentReadSchemaReadonly}
        name={name}
        translationKey="comments"
        query={query as ListProps<CommentRead>["query"]}
        options={options}
        dataMapper={dataMapper}
        tableFeatures={tableFeatures}
        columnOverrides={columnOverrides}
      >
        {title && (
          <div className="py-2">
            <Title>{title}</Title>
          </div>
        )}
        <DataTableQueryContextProvider value={queryValue}>
          <QueryTable.Content>
            <QueryTable.Status />
          </QueryTable.Content>
        </DataTableQueryContextProvider>
      </QueryTable>
      {footer != null ? <div className="mt-2">{footer}</div> : null}
    </div>
  );
}
